/**
 * The Live summary's words (20/10/2026), pure: how far the day is, when the
 * next drop goes and how long ago the last one went, and what needs a
 * look — so the tiles and their spec say the same thing.
 * For Media Devoted | Created By Ciro Autiero
 */
import { usd } from '@/lib/reporting-currency';

import { inWords } from '../../board-header/facts/window-now';
import type { LiveTotals } from './live-totals';

export type Tone = 'muted' | 'live' | 'success' | 'warning';

const n = (x: number) => x.toLocaleString('en-US');

export function dropsStatus(t: LiveTotals): { text: string; tone: Tone } {
  if (t.drops.planned === 0) return { text: 'nothing planned today', tone: 'muted' };
  if (t.drops.sent === t.drops.planned) return { text: 'every drop went', tone: 'success' };
  if (t.drops.sent > 0) return { text: `${n(t.drops.planned - t.drops.sent)} to go`, tone: 'live' };
  return { text: `${n(t.drops.planned)} to go`, tone: 'muted' };
}

/** "est $359 of $684" — the day's spend so far and its full estimate; null without a price (US dollars, 28/10/2026). */
export function costWords(t: LiveTotals): string | null {
  if (t.cost.planned === null) return null;
  const spent = t.cost.spent === null ? '—' : usd(t.cost.spent, 0);
  return `est ${spent} of ${usd(t.cost.planned, 0)}`;
}

/** "in 2 h 15 min" / "now" for the next drop; "none left today" without one. */
export function nextWords(t: LiveTotals, now: Date): { text: string; tone: Tone } {
  if (!t.next) return { text: t.drops.planned > 0 && t.drops.sent === t.drops.planned ? 'the day is done' : 'none left today', tone: 'muted' };
  const min = Math.round((new Date(t.next.atUtc).getTime() - now.getTime()) / 60_000);
  return min <= 0 ? { text: 'now', tone: 'live' } : { text: `in ${inWords(min)}`, tone: 'live' };
}

/** "12 min ago" for the last send; null when nothing went. */
export function lastWords(t: LiveTotals, now: Date): string | null {
  if (!t.last) return null;
  const min = Math.max(0, Math.round((now.getTime() - new Date(t.last.atUtc).getTime()) / 60_000));
  return min === 0 ? 'just now' : `${inWords(min)} ago`;
}

/** What needs a look: due and not sent, held by auto-send, stopped by a verdict. */
export function lookWords(t: LiveTotals): { count: number; text: string; tone: Tone } {
  const parts = [t.overdue > 0 ? `${n(t.overdue)} due` : '', t.held > 0 ? `${n(t.held)} held` : '', t.left > 0 ? `${n(t.left)} left for you` : '', t.stopped > 0 ? `${n(t.stopped)} stopped` : ''].filter(Boolean);
  const count = t.overdue + t.held + t.left + t.stopped;
  return count === 0 ? { count, text: 'nothing needs a look', tone: 'success' } : { count, text: parts.join(' · '), tone: 'warning' };
}
